import React, { useState, useEffect, useMemo } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  useTheme,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DownloadIcon from '@mui/icons-material/Download';
import AbsenceRecordsModal from '../AbsenceRecordsModal';
import {
  getEmployeeAbsenceSummaries,
  getHighAbsenceEmployees,
  getEmployeeLatenessSummaries,
  getEmployeeSicknessSummaries,
  getEmployeePersonalCommitmentsSummaries,
  getHighLatenessEmployees,
  getHighPersonalCommitmentEmployees,
  getHighSicknessEmployees,
} from '../../services/absenceAPIs';
import { useOffices } from '../../offices';
import { exportToExcel } from '../../utils/excelExport';

// Sort Arrow Component
const SortArrow = ({ active, direction }) => {
  if (!active) return null;
  return (
    <span style={{ marginLeft: 6, fontWeight: 'bold', color: 'inherit' }}>
      {direction === 'asc' ? 'up' : 'down'}
    </span>
  );
};

const AbsenceAnalyticsTables = ({ all = false, lateness = false, sickness = false, personal = false }) => {
  const theme = useTheme();
  const [analyticsData, setAnalyticsData] = useState([]);
  const [highAbsenceData, setHighAbsenceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOffice, setSelectedOffice] = useState('');
  const [sortField, setSortField] = useState('absenceRate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const { offices } = useOffices();

  useEffect(() => {
    const loadAnalyticsData = async () => {
      try {
        setLoading(true);
        let data, highData;

        if (lateness) {
          data = await getEmployeeLatenessSummaries();
          highData = await getHighLatenessEmployees();
        } else if (sickness) {
          data = await getEmployeeSicknessSummaries();
          highData = await getHighSicknessEmployees();
        } else if (personal) {
          data = await getEmployeePersonalCommitmentsSummaries();
          highData = await getHighPersonalCommitmentEmployees();
        } else {
          data = await getEmployeeAbsenceSummaries();
          highData = await getHighAbsenceEmployees();
        }

        setAnalyticsData(data);
        setHighAbsenceData(highData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadAnalyticsData();
  }, [lateness, sickness, personal]);

  // Sort Click Handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      const highFirst = [
        'absenceRate',
        'absenceRecordsCount',
        'totalAbsenceHours',
        'totalWorkedHours',
        'totalWorkedDays',
        'differenceFromNormal',
      ].includes(field);
      setSortDirection(highFirst ? 'desc' : 'asc');
    }
  };

  // Filter & Sort Logic
  const filteredAnalyticsData = useMemo(() => {
    const filtered = [...analyticsData].filter(emp => {
      const matchesSearch = emp.employeeName.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesOffice = !selectedOffice || emp.employeeOffice?.id === selectedOffice;
      return matchesSearch && matchesOffice;
    });

    return filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle nested office name
      if (sortField === 'employeeOffice') {
        aValue = a.employeeOffice?.name || '';
        bValue = b.employeeOffice?.name || '';
      }

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue === undefined || aValue === null) aValue = 0;
      if (bValue === undefined || bValue === null) bValue = 0;

      return sortDirection === 'desc'
        ? (bValue > aValue ? 1 : -1)
        : (aValue > bValue ? 1 : -1);
    });
  }, [analyticsData, searchTerm, selectedOffice, sortField, sortDirection]);

  const filteredHighAbsenceData = useMemo(() => {
    const filtered = [...highAbsenceData].filter(emp => {
      const matchesSearch = emp.employeeName.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesOffice = !selectedOffice || emp.employeeOffice?.id === selectedOffice;
      return matchesSearch && matchesOffice;
    });

    return filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === 'employeeOffice') {
        aValue = a.employeeOffice?.name || '';
        bValue = b.employeeOffice?.name || '';
      }

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue === undefined || aValue === null) aValue = 0;
      if (bValue === undefined || bValue === null) bValue = 0;

      return sortDirection === 'desc'
        ? (bValue > aValue ? 1 : -1)
        : (aValue > bValue ? 1 : -1);
    });
  }, [highAbsenceData, searchTerm, selectedOffice, sortField, sortDirection]);

  // Export Functions
  const exportAnalyticsToExcel = () => {
    const dataToExport = filteredAnalyticsData.map(emp => ({
      'Employee Name': emp.employeeName,
      'Office': emp.employeeOffice?.name || 'N/A',
      'Date of Joining': new Date(emp.dateOfJoining).toLocaleDateString(),
      ...(lateness || personal
        ? { 'Total Worked Days': emp.totalWorkedDays?.toFixed(0) }
        : {
          'Total Worked Hours': emp.totalWorkedHours.toFixed(0),
          'Total Absence Hours': emp.totalAbsenceHours.toFixed(1),
        }
      ),
      'Absence Rate (%)': emp.absenceRate.toFixed(2),
      'Safe Rate (%)': emp.normalRate.toFixed(2),
      'Exceeded By (%)': emp.differenceFromNormal.toFixed(2),
      'Records Count': emp.absenceRecordsCount,
    }));
    exportToExcel(dataToExport, 'Employee_Analytics.xlsx', 'Analytics');
  };

  const exportHighAbsenceToExcel = () => {
    const dataToExport = filteredHighAbsenceData.map(emp => ({
      'Employee Name': emp.employeeName,
      'Office': emp.employeeOffice?.name || 'N/A',
      'Date of Joining': new Date(emp.dateOfJoining).toLocaleDateString(),
      ...(lateness || personal
        ? { 'Total Worked Days': emp.totalWorkedDays?.toFixed(0) || 'N/A' }
        : {
          'Total Worked Hours': emp.totalWorkedHours.toFixed(0),
          'Total Absence Hours': emp.totalAbsenceHours.toFixed(1),
        }
      ),
      'Absence Rate (%)': emp.absenceRate.toFixed(2),
      'Records Count': emp.absenceRecordsCount,
    }));
    exportToExcel(dataToExport, 'High_Absence_Employees.xlsx', 'High Absence');
  };

  // Date Utilities
  const formatDate = (date) => new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  const getAdjustedDate = (dateString) => {
    const inputDate = new Date(dateString);
    const currentYear = new Date().getFullYear();
    const yearStart = new Date(currentYear, 0, 1);
    return inputDate < yearStart ? formatDate(yearStart) : formatDate(inputDate);
  };

  // Modal Handlers
  const handleOpenModal = (employee) => {
    setSelectedEmployee(employee);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedEmployee(null);
  };

  const getAbsenceType = () => {
    if (lateness) return 'LATENESS';
    if (sickness) return 'SICKNESS';
    if (personal) return 'PERSONAL';
    return 'ABSENCE';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }

  const styles = {
    accordion: { mb: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderRadius: '8px !important', '&:before': { display: 'none' } },
    tableContainer: { maxHeight: 400, borderRadius: '8px' },
    tableHeader: { backgroundColor: theme.palette.primary.light, '& th': { fontWeight: 'bold', color: theme.palette.primary.contrastText } },
    warningRow: { backgroundColor: 'var(--warning-light, #fff3e0)' },
    highRateChip: { backgroundColor: '#ff6b6b', color: 'white', fontWeight: 'bold' },
    normalRateChip: { backgroundColor: '#4caf50', color: 'white' },
    filterBar: { display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' },
  };

  return (
    <>
      <Box sx={{ mb: 2 }}>
        {/* All Employees Analytics */}
        <Accordion sx={styles.accordion}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon color="primary" />
                <Typography sx={{ fontWeight: 'bold', fontSize: '16px' }}>Employee Rates</Typography>
                <Chip label={`${filteredAnalyticsData.length} employees`} size="small" variant="outlined" />
              </Box>
              <Button variant="outlined" startIcon={<DownloadIcon />} size="small" onClick={exportAnalyticsToExcel}>
                Export Excel
              </Button>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={styles.filterBar}>
              <TextField size="small" label="Search by Employee Name" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} variant="outlined" />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Office</InputLabel>
                <Select value={selectedOffice} onChange={(e) => setSelectedOffice(e.target.value)} label="Office">
                  <MenuItem value="">All Offices</MenuItem>
                  {offices.map(office => (
                    <MenuItem key={office.id} value={office.id}>{office.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {filteredAnalyticsData.length === 0 ? (
              <Alert severity="info" sx={{ m: 2 }}>No employees match your filters.</Alert>
            ) : (
              <TableContainer component={Paper} sx={styles.tableContainer}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow sx={styles.tableHeader}>
                      <TableCell onClick={() => handleSort('employeeName')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                        Employee <SortArrow active={sortField === 'employeeName'} direction={sortDirection} />
                      </TableCell>
                      <TableCell onClick={() => handleSort('employeeOffice')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                        Office <SortArrow active={sortField === 'employeeOffice'} direction={sortDirection} />
                      </TableCell>
                      <TableCell align="center" onClick={() => handleSort('dateOfJoining')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                        Time Interval Start <SortArrow active={sortField === 'dateOfJoining'} direction={sortDirection} />
                      </TableCell>
                      {!lateness && !personal && (
                        <TableCell align="center" onClick={() => handleSort('totalAbsenceHours')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                          Total Absence Hours <SortArrow active={sortField === 'totalAbsenceHours'} direction={sortDirection} />
                        </TableCell>
                      )}
                      <TableCell
                        align="center"
                        onClick={() => handleSort(lateness || personal ? 'totalWorkedDays' : 'totalWorkedHours')}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        {(lateness || personal) ? 'Total Worked Days' : 'Total Worked Hours'}
                        <SortArrow
                          active={sortField === (lateness || personal ? 'totalWorkedDays' : 'totalWorkedHours')}
                          direction={sortDirection}
                        />
                      </TableCell>
                      {(lateness || sickness || personal) && (
                        <TableCell align="center" onClick={() => handleSort('absenceRate')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                          {lateness ? 'Lateness Rate' : sickness ? 'Sickness Rate' : 'PC Rate'}
                          <SortArrow active={sortField === 'absenceRate'} direction={sortDirection} />
                        </TableCell>
                      )}
                      <TableCell align="center" onClick={() => handleSort('normalRate')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                        Safe Rate <SortArrow active={sortField === 'normalRate'} direction={sortDirection} />
                      </TableCell>
                      <TableCell align="center" onClick={() => handleSort('differenceFromNormal')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                        Exceeded by <SortArrow active={sortField === 'differenceFromNormal'} direction={sortDirection} />
                      </TableCell>
                      <TableCell align="center" onClick={() => handleSort('absenceRecordsCount')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                        Records Count <SortArrow active={sortField === 'absenceRecordsCount'} direction={sortDirection} />
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAnalyticsData.map((employee) => (
                      <TableRow key={employee.employeeId} onClick={() => handleOpenModal(employee)} style={{ cursor: 'pointer' }} hover>
                        <TableCell><Typography variant="body2" fontWeight="medium">{employee.employeeName}</Typography></TableCell>
                        <TableCell><Typography variant="body2" fontWeight="medium">{employee.employeeOffice?.name || 'N/A'}</Typography></TableCell>
                        <TableCell align="center">{getAdjustedDate(employee.dateOfJoining)}</TableCell>
                        {!lateness && !personal && (
                          <TableCell align="center">
                            <Typography variant="body2" color="error.main" fontWeight="bold">{employee.totalAbsenceHours.toFixed(1)}h</Typography>
                          </TableCell>
                        )}
                        <TableCell align="center">
                          {(lateness || personal) ? `${employee.totalWorkedDays?.toFixed(0) || 0}d` : `${employee.totalWorkedHours.toFixed(0)}h`}
                        </TableCell>
                        {(lateness || sickness || personal) && (
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight="bold" color={employee.absenceRate > 2 ? 'error' : 'success.main'}>
                              {employee.absenceRate.toFixed(2)}%
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="bold" color="success.main">{employee.normalRate.toFixed(2)}%</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="bold" color={employee.differenceFromNormal > 0 ? 'error' : 'success.main'}>
                            {employee.differenceFromNormal.toFixed(2)}%
                          </Typography>
                        </TableCell>
                        <TableCell align="center">{employee.absenceRecordsCount}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </AccordionDetails>
        </Accordion>

        {/* High Absence Employees */}
        {/* <Accordion sx={styles.accordion}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon color="error" />
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'error.main' }}>High Rated Employees</Typography>
                <Chip label={`${filteredHighAbsenceData.length} employees`} size="small" color="error" variant="outlined" />
              </Box>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                size="small"
                onClick={exportHighAbsenceToExcel}
                disabled={filteredHighAbsenceData.length === 0}
              >
                Export Excel
              </Button>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {filteredHighAbsenceData.length === 0 ? (
              <Alert severity="info" sx={{ m: 2 }}>No employees with high absence rates found.</Alert>
            ) : (
              <TableContainer component={Paper} sx={styles.tableContainer}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow sx={{ ...styles.tableHeader, backgroundColor: '#ffebee' }}>
                      <TableCell onClick={() => handleSort('employeeName')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                        Employee <SortArrow active={sortField === 'employeeName'} direction={sortDirection} />
                      </TableCell>
                      <TableCell align="center" onClick={() => handleSort('dateOfJoining')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                        Date of Joining <SortArrow active={sortField === 'dateOfJoining'} direction={sortDirection} />
                      </TableCell>
                      {!lateness && !personal && (
                        <TableCell align="center" onClick={() => handleSort('totalAbsenceHours')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                          Total Absence Hours <SortArrow active={sortField === 'totalAbsenceHours'} direction={sortDirection} />
                        </TableCell>
                      )}
                      <TableCell
                        align="center"
                        onClick={() => handleSort(lateness || personal ? 'totalWorkedDays' : 'totalWorkedHours')}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        {(lateness || personal) ? 'Total Worked Days' : 'Total Worked Hours'}
                        <SortArrow
                          active={sortField === (lateness || personal ? 'totalWorkedDays' : 'totalWorkedHours')}
                          direction={sortDirection}
                        />
                      </TableCell>
                      {(lateness || sickness || personal) && (
                        <TableCell align="center" onClick={() => handleSort('absenceRate')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                          {lateness ? 'Lateness Rate' : sickness ? 'Sickness Rate' : 'PC Rate'}
                          <SortArrow active={sortField === 'absenceRate'} direction={sortDirection} />
                        </TableCell>
                      )}
                      <TableCell align="center" onClick={() => handleSort('absenceRecordsCount')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                        Records Count <SortArrow active={sortField === 'absenceRecordsCount'} direction={sortDirection} />
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredHighAbsenceData.map((employee) => (
                      <TableRow
                        key={employee.employeeId}
                        sx={styles.warningRow}
                        hover
                        onClick={() => handleOpenModal(employee)}
                        style={{ cursor: 'pointer' }}
                      >
                        <TableCell><Typography variant="body2" fontWeight="bold">{employee.employeeName}</Typography></TableCell>
                        <TableCell align="center">{formatDate(employee.dateOfJoining)}</TableCell>
                        {!lateness && !personal && (
                          <TableCell align="center">
                            <Typography variant="body2" color="error.main" fontWeight="bold">{employee.totalAbsenceHours.toFixed(1)}h</Typography>
                          </TableCell>
                        )}
                        <TableCell align="center">
                          {(lateness || personal) ? `${employee.totalWorkedDays?.toFixed(0) || 0}d` : `${employee.totalWorkedHours.toFixed(0)}h`}
                        </TableCell>
                        {(lateness || sickness || personal) && (
                          <TableCell align="center">
                            <Typography variant="body2" color="error" fontWeight="bold">{employee.absenceRate.toFixed(2)}%</Typography>
                          </TableCell>
                        )}
                        <TableCell align="center">{employee.absenceRecordsCount}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </AccordionDetails>
        </Accordion> */}

        {/* Modal */}
        {selectedEmployee && (
          <AbsenceRecordsModal
            open={modalOpen}
            onClose={handleCloseModal}
            employeeId={selectedEmployee.employeeId}
            employeeName={selectedEmployee.employeeName}
            absenceType={getAbsenceType()}
          />
        )}
      </Box>

      {/* Embedded CSS */}
      <style jsx>{`
        :root {
          --oc-gray-0: #f8f9fa; --oc-gray-1: #f1f3f5; --oc-gray-2: #e9ecef; --oc-gray-3: #dee2e6;
          --oc-gray-4: #ced4da; --oc-gray-5: #adb5bd; --oc-gray-6: #868e96; --oc-gray-7: #495057;
          --oc-gray-8: #343a40; --oc-gray-9: #212529;
          --oc-blue-0: #e7f5ff; --oc-blue-1: #d0ebff; --oc-blue-2: #a5d8ff; --oc-blue-3: #74c0fc;
          --oc-blue-4: #4dabf7; --oc-blue-5: #339af0; --oc-blue-6: #228be6; --oc-blue-7: #1c7ed6;
          --oc-blue-8: #1971c2; --oc-blue-9: #1864ab;
          --oc-red-0: #fff5f5; --oc-red-1: #ffe3e3; --oc-red-2: #ffc9c9; --oc-red-3: #ffa8a8;
          --oc-red-5: #ff6b6b; --oc-red-6: #fa5252; --oc-red-7: #f03e3e; --oc-red-8: #e03131;
          --oc-red-9: #c92a2a;
          --oc-orange-0: #fff4e6; --oc-orange-1: #ffe8cc; --oc-yellow-0: #fff9db; --oc-yellow-1: #fff3bf;
          --oc-green-0: #ebfbee; --oc-green-1: #d3f9d8; --oc-green-3: #8ce99a; --oc-green-9: #2b8a3e;
        }

        .MuiAccordion-root { background: ${theme.palette.background.paper} !important; border-radius: 12px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important; margin-bottom: 16px !important; overflow: hidden !important; }
        .MuiAccordion-root:before { display: none !important; }
        .MuiAccordionSummary-root { background: var(--card-bg, ${theme.palette.mode === 'dark' ? '#1e293b' : '#f8fafc'}) !important; border-bottom: 1px solid var(--oc-gray-2) !important; padding: 8px 16px !important; min-height: 30px !important; }
        .MuiAccordionSummary-root:hover { background: var(--hover-bg, ${theme.palette.mode === 'dark' ? '#334155' : '#f1f5f9'}) !important; }
        .MuiAccordionSummary-root .MuiTypography-h5 { color: ${theme.palette.mode === 'dark' ? '#f8fafc' : '#1e293b'} !important; }
        .MuiAccordionDetails-root { padding: 16px !important; background: var(--card-bg, ${theme.palette.mode === 'dark' ? '#1e293b' : 'white'}) !important; }

        .MuiTypography-h5 { font-size: 20px !important; font-weight: 700 !important; color: ${theme.palette.mode === 'dark' ? '#f8fafc' : '#1e293b'} !important; letter-spacing: -0.3px !important; }

        .MuiChip-root { font-weight: 600 !important; border-radius: 16px !important; }
        .MuiChip-label { color: var(--text-primary, white) !important; }
        .MuiChip-outlined { border-width: 1.5px !important; border-color: var(--border-color, #475569) !important; background: var(--card-bg, #1e293b) !important; }
        .MuiChip-colorError { background: linear-gradient(135deg, var(--oc-red-9) 0%, #7f1d1d 100%) !important; color: white !important; border-color: var(--oc-red-9) !important; }

        .MuiOutlinedInput-root { border-radius: 8px !important; }
        .MuiOutlinedInput-root fieldset { border-color: var(--oc-gray-3) !important; border-width: 1.5px !important; }
        .MuiOutlinedInput-root:hover fieldset { border-color: var(--oc-blue-5) !important; }
        .MuiOutlinedInput-root.Mui-focused fieldset { border-color: var(--oc-blue-6) !important; border-width: 2px !important; }
        .MuiInputLabel-root { color: var(--oc-gray-6) !important; font-weight: 500 !important; }
        .MuiInputLabel-root.Mui-focused { color: var(--oc-blue-6) !important; }

        .MuiButton-root { border-radius: 8px !important; text-transform: none !important; font-weight: 600 !important; padding: 8px 16px !important; }
        .MuiButton-outlined { border-width: 1.5px !important; border-color: var(--oc-gray-4) !important; color: var(--oc-gray-8) !important; }
        .MuiButton-outlined:hover { border-color: var(--oc-blue-6) !important; background: var(--oc-blue-0) !important; color: var(--oc-blue-8) !important; transform: translateY(-1px); box-shadow: 0 4px 8px rgba(74, 123, 167, 0.2) !important; }

        .MuiTableContainer-root { border-radius: 12px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important; background: ${theme.palette.background.paper} !important; }
        .MuiTableHead-root .MuiTableRow-root { background: #4A7BA7 !important; }
        .MuiTableHead-root .MuiTableCell-root { background: #4A7BA7 !important; color: white !important; font-weight: 700 !important; text-transform: uppercase !important; letter-spacing: 0.5px !important; font-size: 13px !important; padding: 16px 20px !important; }

        .MuiTableBody-root .MuiTableRow-root { transition: all 0.2s ease !important; border-bottom: 1px solid var(--oc-gray-2) !important; }
        .MuiTableBody-root .MuiTableRow-root:hover { background: ${theme.palette.mode === 'dark' ? 'rgba(74, 123, 167, 0.15)' : 'var(--oc-blue-0)'} !important; transform: translateX(2px); cursor: pointer; }
        .MuiTableBody-root .MuiTableCell-root { padding: 16px 20px !important; color: ${theme.palette.text.primary} !important; font-size: 14px !important; font-weight: 500 !important; }

        .MuiAlert-root { border-radius: 12px !important; font-weight: 500 !important; box-shadow: 0 2px 8px rgba(0,0,0,0.08) !important; border: 1.5px solid !important; }
        .MuiAlert-standardSuccess { background: linear-gradient(135deg, var(--oc-green-0) 0%, var(--oc-green-1) 100%) !important; color: var(--oc-green-9) !important; border-color: var(--oc-green-3) !important; }
        .MuiAlert-standardError { background: linear-gradient(135deg, var(--oc-red-0) 0%, var(--oc-red-1) 100%) !important; color: var(--oc-red-9) !important; border-color: var(--oc-red-3) !important; }
        .MuiAlert-standardInfo { background: linear-gradient(135deg, var(--oc-blue-0) 0%, var(--oc-blue-1) 100%) !important; color: var(--oc-blue-9) !important; border-color: var(--oc-blue-3) !important; }

        .MuiSvgIcon-colorPrimary { color: #4A7BA7 !important; }
        .MuiSvgIcon-colorError { color: var(--oc-red-6) !important; }
        .MuiCircularProgress-root { color: #4A7BA7 !important; }

        @keyframes slideIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .MuiTableBody-root .MuiTableRow-root { animation: slideIn 0.3s ease; }
      `}</style>
    </>
  );
};

export default AbsenceAnalyticsTables;